import { useMemo, useState } from 'react'
import { Layers, Plus, Search, X, Zap } from 'lucide-react'
import {
  ActionType,
  AuthoringContext,
  resolveGraphFrontendComponentAllowlist,
  type NodeActionGroup,
  type WorkflowActionCatalogEntry,
} from '@super/shared-types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { describeAction } from '@/lib/actions/describe'
import { useDirectory } from '@/contexts/directory.context'

/** Shared source of truth with the save-time validator in
 *  `findInvalidActionNode`. Keeps picker and validator in lockstep so a
 *  pickable group never fails the save gate on an unsupported type (e.g.
 *  legacy v2 PHONE_CALL rows that live in the catalog but have no v3
 *  runtime dispatcher). */
const SUPPORTED_ACTION_TYPES = new Set<ActionType>(
  resolveGraphFrontendComponentAllowlist(
    AuthoringContext.SUPER_ADMIN,
  ).allowedActionTypes,
)

interface NodeAgentActionPickerProps {
  /** The named action groups the admin loaded for the picker. */
  availableGroups: NodeActionGroup[]
  /** Named catalog actions the author can attach directly. Selecting one
   *  creates a single-member group on the fly. TRANSFER rows must be
   *  excluded by the caller. */
  availableActions: WorkflowActionCatalogEntry[]
  /** Called when the author picks an existing group — the parent lands on
   *  the summary card. */
  onPickGroup: (groupId: string) => void
  /** Called when the author picks an individual action. The parent creates
   *  a NodeActionGroup around it, then attaches the new group to the node. */
  onPickAction: (workflowActionId: string) => Promise<void> | void
  /** Called when the author clicks "Create new action". The parent enters
   *  the draft edit view with blank defaults — no POST yet. */
  onStartDraft: () => void
}

/** Empty-state picker for an action node. Searches named action groups AND
 *  individual catalog actions in parallel; the two kinds render in separate
 *  sections with their own icons so the author sees which is which. */
export function NodeAgentActionPicker({
  availableGroups,
  availableActions,
  onPickGroup,
  onPickAction,
  onStartDraft,
}: NodeAgentActionPickerProps) {
  const { roles, staff } = useDirectory()
  const [query, setQuery] = useState('')
  const [picking, setPicking] = useState<string | null>(null)

  const groupOptions = useMemo(() => {
    return availableGroups.filter(group => {
      if (!group.name) return false
      if (group.members.length === 0) return false
      // Every member must be a v3-supported type. One unsupported member
      // (e.g. a legacy v2 PHONE_CALL row) makes the whole group unpickable.
      return group.members.every(m =>
        SUPPORTED_ACTION_TYPES.has(m.workflowAction.actionType as ActionType),
      )
    })
  }, [availableGroups])

  const actionOptions = useMemo(() => {
    return availableActions.filter(entry => {
      if (!SUPPORTED_ACTION_TYPES.has(entry.actionType)) return false
      return (entry.resolvedName ?? entry.name ?? '').length > 0
    })
  }, [availableActions])

  const filteredGroups = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return groupOptions
    return groupOptions.filter(group => {
      const row = group.members[0]?.workflowAction
      const summary = row
        ? describeAction(
            {
              actionType: row.actionType as ActionType,
              actionSubtype: row.actionSubtype ?? null,
              parameters: row.parameters,
              contacts: row.contacts,
            },
            { roles, staff },
          )
        : ''
      const haystack =
        `${group.name ?? ''} ${group.description ?? ''} ${summary}`.toLowerCase()
      return haystack.includes(needle)
    })
  }, [query, groupOptions, roles, staff])

  const filteredActions = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) return actionOptions
    return actionOptions.filter(entry => {
      const summary = describeAction(
        {
          actionType: entry.actionType,
          actionSubtype: entry.actionSubtype,
          parameters: entry.parameters,
          contacts: entry.contacts,
        },
        { roles, staff },
      )
      const label = entry.resolvedName ?? entry.name ?? ''
      const haystack =
        `${label} ${entry.description ?? ''} ${summary}`.toLowerCase()
      return haystack.includes(needle)
    })
  }, [query, actionOptions, roles, staff])

  const nothingLoaded = groupOptions.length + actionOptions.length === 0
  const nothingFiltered = filteredGroups.length + filteredActions.length === 0

  async function handlePickAction(workflowActionId: string) {
    setPicking(workflowActionId)
    try {
      await onPickAction(workflowActionId)
    } finally {
      setPicking(null)
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-muted-foreground">
        Pick an existing action, an action group, or create a new one. The
        post-call phase runs it when this node is downstream of End
        Conversation.
      </p>

      <div className="relative">
        <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Search actions and action groups"
          className="h-8 pl-7 pr-7 text-xs"
          autoFocus
        />
        {query && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute right-0.5 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground"
            onClick={() => setQuery('')}
          >
            <X className="h-3 w-3" />
          </Button>
        )}
      </div>

      {nothingLoaded ? (
        <p className="text-xs text-muted-foreground">
          No shared actions yet. Create one for this flow and it becomes
          available to every graph in this company.
        </p>
      ) : nothingFiltered ? (
        <p className="text-xs text-muted-foreground">
          No matches for &quot;{query}&quot;.
        </p>
      ) : (
        <div className="space-y-3 max-h-80 overflow-y-auto">
          {filteredGroups.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-1">
                Action groups
              </div>
              {filteredGroups.map(group => {
                const row = group.members[0]?.workflowAction
                const summary = row
                  ? describeAction(
                      {
                        actionType: row.actionType as ActionType,
                        actionSubtype: row.actionSubtype ?? null,
                        parameters: row.parameters,
                        contacts: row.contacts,
                      },
                      { roles, staff },
                    )
                  : null
                return (
                  <button
                    key={group.id}
                    type="button"
                    onClick={() => onPickGroup(group.id)}
                    className="w-full text-left rounded-md border border-border px-3 py-2 text-xs hover:bg-accent disabled:opacity-50"
                  >
                    <div className="flex items-center gap-1.5">
                      <Layers className="h-3 w-3 flex-shrink-0 text-primary" />
                      <div className="font-medium truncate">{group.name}</div>
                    </div>
                    {group.description && (
                      <div className="text-muted-foreground truncate pl-4">
                        {group.description}
                      </div>
                    )}
                    {summary && (
                      <div className="text-muted-foreground truncate pl-4">
                        {summary}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          )}

          {filteredGroups.length > 0 && filteredActions.length > 0 && (
            <hr className="border-border" />
          )}

          {filteredActions.length > 0 && (
            <div className="space-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-1">
                Individual actions
              </div>
              {filteredActions.map(entry => {
                const summary = describeAction(
                  {
                    actionType: entry.actionType,
                    actionSubtype: entry.actionSubtype,
                    parameters: entry.parameters,
                    contacts: entry.contacts,
                  },
                  { roles, staff },
                )
                const label = entry.resolvedName ?? entry.name ?? 'Untitled'
                const isBusy = picking === entry.id
                return (
                  <button
                    key={entry.id}
                    type="button"
                    disabled={picking !== null}
                    onClick={() => void handlePickAction(entry.id)}
                    className="w-full text-left rounded-md border border-border px-3 py-2 text-xs hover:bg-accent disabled:opacity-50"
                  >
                    <div className="flex items-center gap-1.5">
                      <Zap className="h-3 w-3 flex-shrink-0 text-primary" />
                      <div className="font-medium truncate">{label}</div>
                      {isBusy && (
                        <span className="text-[10px] text-muted-foreground">
                          Attaching…
                        </span>
                      )}
                    </div>
                    {entry.description && (
                      <div className="text-muted-foreground truncate pl-4">
                        {entry.description}
                      </div>
                    )}
                    {summary && (
                      <div className="text-muted-foreground truncate pl-4">
                        {summary}
                      </div>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 text-xs"
          onClick={onStartDraft}
        >
          <Plus className="h-3 w-3 mr-1" />
          Create new action
        </Button>
      </div>
    </div>
  )
}
